import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router";

import { App } from "./App";
import { shouldRetryQuery } from "@/lib/query-freshness";
import { startThemeSync } from "@/lib/theme";
// The three self-hosted typefaces, before the stylesheet that uses them.
// index.html used to link Google Fonts for these; see app/fonts.css.
import "./fonts.css";
import "./styles.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Admin data is operational: a stale order status is worse than a
      // refetch. Short staleness, refetch on focus, and — through
      // `shouldRetryQuery` — no retry at all on a status the server has
      // already ruled on. `retry: 1` counted failures without reading the
      // error, so a 401/403/404 cost a second request and about a second of
      // delay before the sign-in screen or the error card appeared (A3-15).
      //
      // This is the FLOOR, not the policy. Every read declared in
      // `lib/queries.ts` picks its own window out of `lib/query-freshness.ts`
      // — 15 s for a live list, 30 s for one open record, 60 s and no
      // focus-refetch for an aggregate — because one number for all of them
      // meant a Stripe overview was re-fetched as eagerly as a work queue.
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      retry: shouldRetryQuery,
    },
  },
});

startThemeSync();

const container = document.getElementById("root");
if (!container) throw new Error("Root container is missing from index.html.");

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
